import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PageContainer from '../../components/PageElements/PageContainer/PageContainer';
import config from '../../config';
import { getVenueById, VenueData } from '../../services/venueService';
import { createVisit, VisitData } from '../../services/visitService';
import AuthStep from './steps/AuthStep';
import DietaryStep from './steps/DietaryStep';
import HealthStep from './steps/HealthStep';
import FavoritesStep from './steps/FavoritesStep';
import OAuthConsentStep from './steps/OAuthConsentStep';
import PlatefullAILogo from '../../images/PlatefullAILogoNB.svg';
import '../../constants/theme.css';
import styles from './Onboard.module.css';

export type OnboardStep = 'auth' | 'dietary' | 'health' | 'favorites' | 'oauth_consent' | 'complete';

interface OnboardData {
  email: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  venueId?: string;
  locationId?: string;
  venueData?: VenueData;
  visitData?: VisitData;
}

const Onboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login, register, getUserPreferencesFromStorage, saveUserPreferencesToStorage, clearUserPreferencesFromStorage } = useAuth();
  
  const [currentStep, setCurrentStep] = useState<OnboardStep>(() => {
    // If user is already logged in and has venue parameters, start at dietary step
    // This will be updated by the useEffect below, but this prevents the flash
    return 'auth';
  });
  const [isCreatingVisit, setIsCreatingVisit] = useState(false);
  const [oauthConsentShown, setOauthConsentShown] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Debug step changes

  // Extract URL parameters for venue and location
  const venueId = searchParams.get('venue-id') || searchParams.get('venue') || undefined;
  const locationId = searchParams.get('location-id') || searchParams.get('location') || searchParams.get('table') || undefined; // 'table' is treated as location
  const mode = searchParams.get('mode'); // Check for register mode
  
  // Extract OAuth parameters
  const clientId = searchParams.get('client_id');
  const state = searchParams.get('state');
  const redirectUriParam = searchParams.get('redirect_uri');
  // OAuth flow is determined by presence of client_id
  const oauthFlow = !!clientId;
  const [redirectUri, setRedirectUri] = useState<string | null>(null);
  const [loadingRedirectUri, setLoadingRedirectUri] = useState(false);
  
  // Fetch redirect URI from backend if we have client_id but no redirect_uri; prefer explicit URL param if provided
  useEffect(() => {
    if (!clientId || redirectUri || loadingRedirectUri) return;
    
    // If redirect_uri is explicitly provided in URL, use it immediately
    if (redirectUriParam) {
      setRedirectUri(redirectUriParam);
      return;
    }

      setLoadingRedirectUri(true);
      fetch(`${config.API_BASE_URL}/oauth/client/${clientId}/redirect-uris`)
        .then(res => res.json())
        .then(data => {
          if (data.redirect_uris && data.redirect_uris.length > 0) {
            // Backend already normalizes URIs with https:// if missing
            const uri = data.redirect_uris[0];
            setRedirectUri(uri);
          } else {
            console.warn('🔵 Onboard: No redirect URIs found in response');
          }
        })
        .catch(err => {
          console.error('🔵 Onboard: Failed to fetch redirect URIs:', err);
        })
        .finally(() => {
          setLoadingRedirectUri(false);
        });
  }, [clientId, redirectUri, loadingRedirectUri, redirectUriParam]);
  
  // No need to load user data - it's already in user.preferences
  const [onboardData, setOnboardData] = useState<OnboardData>({
    email: '',
    venueId,
    locationId,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isNewUser, setIsNewUser] = useState(mode === 'register');
  // Remove userData state - use user.preferences directly

  // Check if this is a venue check-in flow (used throughout component)
  const hasVenueParams = onboardData.venueId || onboardData.locationId;

  // Fetch venue data if we have a venue ID
  useEffect(() => {
    const fetchVenueData = async () => {
      if (onboardData.venueId) {
        try {
          const venueData = await getVenueById(onboardData.venueId);
          setOnboardData(prev => ({ ...prev, venueData }));
        } catch (error) {
          console.error('Failed to fetch venue data:', error);
          // Continue without venue data - user can still complete onboarding
        }
      }
    };

    fetchVenueData();
  }, [onboardData.venueId]);

  // Create visit immediately if user is already logged in and has venue parameters
  useEffect(() => {
    const createVisitForLoggedInUser = async () => {
      if (user && hasVenueParams && !onboardData.visitData && !isCreatingVisit) {
        setIsCreatingVisit(true);
        
        try {
          // First check if user already has an active visit
          const { getActiveVisit } = await import('../../services/visitService');
          const existingVisit = await getActiveVisit();
          
          if (existingVisit) {
            // Check if the visit is actually still active (not expired)
            const now = new Date();
            const expiresAt = new Date(existingVisit.expires_at);
            
            if (expiresAt < now) {
              // Don't set the expired visit data, let it create a new one
            } else {
              setOnboardData(prev => ({ ...prev, visitData: existingVisit }));
              setIsCreatingVisit(false);
              return;
            }
          }
          
          const visitRequest = {
            venue_id: onboardData.venueId!,
            location_id: onboardData.locationId || undefined
          };
          
          const { createVisit } = await import('../../services/visitService');
          const visit = await createVisit(visitRequest);
          
          // Store visit data for later use
          setOnboardData(prev => ({ ...prev, visitData: visit }));
          
          // Visit created successfully
        } catch (error) {
          console.error('Failed to create visit:', error);
          // Don't block the flow if visit creation fails
        } finally {
          setIsCreatingVisit(false);
        }
      }
    };

    createVisitForLoggedInUser();
  }, [user, hasVenueParams, onboardData.venueId, onboardData.locationId, onboardData.visitData, isCreatingVisit]);

  // Handle OAuth redirect when complete step is reached
  useEffect(() => {
    if (currentStep === 'complete' && clientId && redirectUri && user) {
      
      // Build callback URL with user data
      const callbackUrl = new URL(redirectUri);
      callbackUrl.searchParams.set('code', 'demo-auth-code-123');
      callbackUrl.searchParams.set('email', user.email);
      if (state) callbackUrl.searchParams.set('state', state);
      
      // Redirect after a short delay
      setTimeout(() => {
        window.location.href = callbackUrl.toString();
      }, 2000);
    }
  }, [currentStep, clientId, redirectUri, user, state]);

  // Redirect if already logged in
  useEffect(() => {
    if (user && currentStep === 'auth') {
      // Check if this is an OAuth flow
      if (clientId && redirectUri && !oauthConsentShown) {
        setCurrentStep('oauth_consent');
        setOauthConsentShown(true);
        return;
      }
      
      // User is already logged in, skip to dietary preferences
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep('dietary');
        setIsTransitioning(false);
      }, 500);
    }
  }, [user, currentStep, clientId, redirectUri, oauthConsentShown]);

  const updateOnboardData = (data: Partial<OnboardData>) => {
    setOnboardData(prev => ({ ...prev, ...data }));
  };

  // No need for fetchUserData - preferences are included in login response

  const handleNext = async (stepData: Partial<OnboardData>) => {
    setError('');
    updateOnboardData(stepData);

    try {
      switch (currentStep) {
        case 'auth':
          
          if (isNewUser) {
            // Register new user
            await register(stepData.email!, stepData.password!, stepData.firstName, stepData.lastName);
          } else {
            // Login existing user
            await login(stepData.email!, stepData.password!);
            // User preferences are already included in login response
          }
          
          // Visit creation is handled by the useEffect for logged-in users
          // No need to create visit here in the auth step
          
          // Check if this is an OAuth flow
          if (clientId && redirectUri) {
            setCurrentStep('oauth_consent');
          } else {
            // Always continue to dietary preferences (for normal flows)
            setIsTransitioning(true);
            setTimeout(() => {
              setCurrentStep('dietary');
              setIsTransitioning(false);
            }, 500);
          }
          break;
          
        case 'dietary':
          setIsTransitioning(true);
          setTimeout(() => {
            setCurrentStep('health');
            setIsTransitioning(false);
          }, 500);
          break;
          
        case 'health':
          setIsTransitioning(true);
          setTimeout(() => {
            setCurrentStep('favorites');
            setIsTransitioning(false);
          }, 500);
          break;
          
        case 'favorites':
          setCurrentStep('complete');
          
          // Redirect to main app, venue-specific page, or OAuth client
          setTimeout(() => {
            if (clientId && redirectUri) {
              // OAuth flow - redirect to OAuth client
              const callbackUrl = new URL(redirectUri);
              callbackUrl.searchParams.set('code', 'demo-auth-code-123');
              callbackUrl.searchParams.set('email', user?.email || '');
              if (state) callbackUrl.searchParams.set('state', state);
              
              window.location.href = callbackUrl.toString();
            } else if (hasVenueParams) {
              // Redirect to venue page after successful onboarding
              navigate(`/venue/${onboardData.venueId}`);
            } else {
              navigate('/');
            }
          }, 2000);
          break;
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'An error occurred';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setError('');
    switch (currentStep) {
      case 'health':
        setCurrentStep('dietary');
        break;
      case 'favorites':
        setCurrentStep('health');
        break;
      case 'oauth_consent':
        setCurrentStep('dietary');
        break;
      case 'complete':
        setCurrentStep('favorites');
        break;
    }
  };

  const handleSkip = () => {
    switch (currentStep) {
      case 'dietary':
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentStep('health');
          setIsTransitioning(false);
        }, 500);
        break;
      case 'health':
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentStep('favorites');
          setIsTransitioning(false);
        }, 500);
        break;
      case 'favorites':
        // Check if this is an OAuth flow
        if (clientId && redirectUri) {
          setCurrentStep('oauth_consent');
        } else {
          setCurrentStep('complete');
        }
        
        
        setTimeout(() => {
          if (hasVenueParams) {
            // Redirect to venue page after successful onboarding
            navigate(`/venue/${onboardData.venueId}`);
          } else {
            navigate('/');
          }
        }, 2000);
        break;
    }
  };

  const handleSkipAll = () => {
    setCurrentStep('complete');
    
    setTimeout(() => {
      if (hasVenueParams) {
        // Redirect to venue page after successful onboarding
        navigate(`/venue/${onboardData.venueId}`);
      } else {
        navigate('/');
      }
    }, 2000);
  };

  const handleStepClick = (stepNumber: number) => {
    const stepMap: { [key: number]: OnboardStep } = {
      1: 'auth',
      2: 'dietary', 
      3: 'health',
      4: 'favorites'
    };
    
    const targetStep = stepMap[stepNumber];
    
    // Prevent logged-in users from going back to auth step
    if (user && targetStep === 'auth') {
      return;
    }
    
    if (targetStep && targetStep !== currentStep) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep(targetStep);
        setIsTransitioning(false);
      }, 500);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'auth':
        return (
          <AuthStep
            email={onboardData.email}
            firstName={onboardData.firstName}
            lastName={onboardData.lastName}
            password={onboardData.password}
            onNext={handleNext}
            isLoading={isLoading}
            error={error}
            isNewUser={isNewUser}
            toggleUserType={() => setIsNewUser(!isNewUser)}
            onStepClick={handleStepClick}
          />
        );
      case 'dietary':
        return (
          <div className={`${styles.stepContainer} ${styles.dietaryStep}`}>
            <DietaryStep
              onNext={handleNext}
              onSkip={handleSkip}
              onSkipAll={handleSkipAll}
              isLoading={isLoading}
              error={error}
              user={user}
              onStepClick={handleStepClick}
            />
          </div>
        );
      case 'health':
        return (
          <div className={`${styles.stepContainer} ${styles.healthStep}`}>
            <HealthStep
              onNext={handleNext}
              onBack={handleBack}
              isLoading={isLoading}
              error={error}
              user={user}
              onStepClick={handleStepClick}
            />
          </div>
        );
      case 'favorites':
        return (
          <div className={`${styles.stepContainer} ${styles.favoritesStep}`}>
            <FavoritesStep
              onNext={handleNext}
              onBack={handleBack}
              isLoading={isLoading}
              error={error}
              user={user}
              onStepClick={handleStepClick}
            />
          </div>
        );
      case 'oauth_consent':
        // Use direct OAuth parameters
        const oauthParams = {
          client_id: clientId || '',
          redirect_uri: redirectUri || '',
          state: state || '',
          scope: 'dietary_preferences:read ingredients:read',
          code_challenge: '',
          code_challenge_method: ''
        };

        return (
          <div className={`${styles.stepContainer} ${styles.oauthConsentStep}`}>
            <OAuthConsentStep
              onComplete={(isExistingAuth) => {
                // If user has existing authorization AND complete profile (dietary + health), redirect directly to OAuth client
                if (isExistingAuth && clientId && redirectUri) {
                  const callbackUrl = new URL(redirectUri);
                  callbackUrl.searchParams.set('code', 'demo-auth-code-123');
                  callbackUrl.searchParams.set('email', user?.email || '');
                  if (state) callbackUrl.searchParams.set('state', state);
                  
                  // Redirect immediately
                  window.location.href = callbackUrl.toString();
                } else {
                  // For new authorizations OR incomplete profiles, continue with onboarding flow
                  setCurrentStep('dietary');
                }
              }}
              onBack={handleBack}
              oauthParams={oauthParams}
            />
          </div>
        );
      case 'complete':
        const isVenueCheckin = onboardData.venueId || onboardData.locationId;
        const isOAuthFlow = clientId && redirectUri;
        
        return (
          <div className={styles.completeStep}>
            <div className={styles.completeIcon}>✓</div>
            <h2>You're all set!</h2>
            
            {isVenueCheckin ? (
              <>
                <p>You've been checked in and your preferences have been saved!</p>
                <div className={styles.visitInfo}>
                  <h3>Visit Details</h3>
                  {onboardData.venueData?.name && (
                    <p>📍 Venue: {onboardData.venueData.name}</p>
                  )}
                  {onboardData.locationId && (
                    <p>🪑 Location: {onboardData.locationId}</p>
                  )}
                  {onboardData.visitData && (
                    <>
                      <p>✅ Status: {onboardData.visitData.status}</p>
                      <p>🕐 Check-in: {new Date(onboardData.visitData.check_in_time).toLocaleTimeString()}</p>
                      <p>⏰ Expires: {new Date(onboardData.visitData.expires_at).toLocaleTimeString()}</p>
                    </>
                  )}
                </div>
              </>
            ) : (
              <p>Your preferences have been saved.</p>
            )}
            
            {isOAuthFlow ? (
              <p>Redirecting you back to the website...</p>
            ) : (
              <p>Redirecting you now...</p>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  // Loading screen component
  const LoadingScreen = () => (
    <div className={`${styles.loadingScreen}`}>
      <div className={styles.stepHeader}>
        <div className={styles.logoContainer}>
          <img src={PlatefullAILogo} alt="PLATEFULL AI Logo" className={styles.logoImage} />
        </div>
      </div>
      <div className={styles.loadingContent}>
        <div className={styles.loadingSpinner}></div>
        <p className={styles.loadingText}>Loading...</p>
      </div>
    </div>
  );

  return (
    <PageContainer hideFooter={true}>
      <div className={styles.onboardContainer}>
        <div className={styles.onboardContent}>
          {isTransitioning ? <LoadingScreen /> : renderStep()}
        </div>
      </div>
    </PageContainer>
  );
};

export default Onboard;
